import React from "react";
import { ColumnDef, RowSelectionState } from "@tanstack/react-table";
import {
  DocumentIcon,
  EyeIcon,
  PhotoIcon,
  Squares2X2Icon,
  VideoCameraIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import Table, { Action, BulkAction } from "../common/Table";
import FilePreview from "../common/FilePreview";
import { Creative } from "../../schemas/creativeSchema";
import {
  formatTimestampToDate,
  getCreativeStatus,
} from "../../utils/dateUtils";
import { getFullMediaUrl } from "../../utils/mediaUtils";

const TYPE_TABS = [
  {
    label: "All",
    icon: <Squares2X2Icon className="h-5 w-5 mr-1" />,
    value: "all",
  },
  {
    label: "Videos",
    icon: <VideoCameraIcon className="h-5 w-5 mr-1" />,
    value: "video",
  },
  {
    label: "Images",
    icon: <PhotoIcon className="h-5 w-5 mr-1" />,
    value: "image",
  },
  {
    label: "Documents",
    icon: <DocumentIcon className="h-5 w-5" />,
    value: "document",
  },
];

// Expired is deliberately neutral rather than red: a finished flight is the
// creative doing what it was told, not a fault the operator has to fix.
export const CREATIVE_STATUS_BADGE: Record<
  string,
  { label: string; className: string }
> = {
  ACTIVE: {
    label: "Active",
    className: "bg-success-100 text-success-700",
  },
  UPCOMING: {
    label: "Upcoming",
    className: "bg-blue-100 text-blue-700",
  },
  EXPIRED: {
    label: "Expired",
    className: "bg-gray-100 text-gray-600",
  },
};

export interface CreativeDetailRow {
  label: string;
  value: React.ReactNode;
}

interface CreativeListProps {
  creatives: Creative[];
  isLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  onSearch: (searchTerm: string) => void;

  /** Active media-type tab; undefined means All. */
  activeType?: string;
  onTypeChange: (type?: string) => void;

  /**
   * The row open in the details panel. Controlled rather than internal state:
   * the tenant page feeds the same creative to its edit form.
   */
  selectedCreative: Creative | null;
  onSelectCreative: (creative: Creative | null) => void;

  /**
   * Columns inserted after Name, before the dates — Ad Group and Brand on the
   * tenant side. A brand has no use for either.
   */
  extraColumns?: ColumnDef<Creative>[];
  /** Row actions beyond View. */
  extraActions?: Action<Creative>[];
  bulkActions?: BulkAction[];
  selectable?: boolean;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: React.Dispatch<
    React.SetStateAction<RowSelectionState>
  >;

  /**
   * Rendered after the type tabs: the tenant filter button, the brand portal's
   * status tabs.
   */
  toolbarSlot?: React.ReactNode;
  /** Property rows for the details panel. Each page owns its own set. */
  detailRows?: CreativeDetailRow[];
  /** Rendered beside the details "Properties" heading — the tenant Edit button. */
  detailActions?: React.ReactNode;
}

/**
 * The creative list and its details panel, shared by the tenant page and the
 * brand portal's read-only one.
 *
 * The two screens differ only at the edges — columns, row actions, toolbar and
 * property rows — so those are props, while the table, the type tabs and the
 * preview panel are defined once here. Everything mutating stays in the pages:
 * this component has no mutations of its own, which is what keeps the brand
 * portal read-only by construction rather than by remembering a permission
 * check on every control.
 */
const CreativeList: React.FC<CreativeListProps> = ({
  creatives,
  isLoading,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  onSearch,
  activeType,
  onTypeChange,
  selectedCreative,
  onSelectCreative,
  extraColumns = [],
  extraActions = [],
  bulkActions,
  selectable = false,
  rowSelection,
  onRowSelectionChange,
  toolbarSlot,
  detailRows = [],
  detailActions,
}) => {
  const columns: ColumnDef<Creative>[] = [
    {
      id: "thumbnail",
      header: "Creative",
      cell: ({ row }) => {
        const media = row.original.media?.[0];
        const thumbnailSrc = getFullMediaUrl(
          media?.thumbnailUrl || media?.fileUrl,
        );
        return thumbnailSrc ? (
          <img
            src={thumbnailSrc}
            alt="Creative"
            className="h-14 w-14 rounded-md object-cover border"
          />
        ) : (
          <div className="h-14 w-14 rounded-md border flex items-center justify-center bg-gray-50">
            <DocumentIcon className="h-8 w-8 text-gray-400" />
          </div>
        );
      },
    },
    {
      id: "name",
      header: "Name",
      cell: ({ row }) => row.original.media?.[0]?.fileName || "-",
    },
    ...extraColumns,
    {
      accessorKey: "startDate",
      header: "Start Date",
      cell: ({ getValue }) => {
        const value = getValue() as string;
        return value ? formatTimestampToDate(value) : "-";
      },
    },
    {
      accessorKey: "endDate",
      header: "End Date",
      cell: ({ getValue }) => {
        const value = getValue() as string;
        return value ? formatTimestampToDate(value) : "-";
      },
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }) => {
        const badge =
          CREATIVE_STATUS_BADGE[
            getCreativeStatus(row.original.startDate, row.original.endDate)
          ];
        return (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.className}`}
          >
            {badge.label}
          </span>
        );
      },
    },
  ];

  const actions: Action<Creative>[] = [
    {
      label: "View",
      icon: <EyeIcon className="h-4 w-4" />,
      onClick: (creative: Creative) => onSelectCreative(creative),
      variant: "primary" as const,
    },
    ...extraActions,
  ];

  return (
    <div className="flex flex-col md:flex-row flex-1 gap-4">
      <div
        className={`w-full transition-all duration-300 ${
          selectedCreative ? "md:w-1/2" : "md:w-full"
        }`}
      >
        <Table
          data={creatives}
          columns={columns}
          actions={actions}
          searchable
          pagination
          isLoading={isLoading}
          maxHeight="66vh"
          infinite
          hasNextPage={hasNextPage}
          isFetchingMore={isFetchingNextPage}
          onLoadMore={onLoadMore}
          onSearch={onSearch}
          selectable={selectable}
          getRowId={(item) => item.id}
          rowSelection={rowSelection}
          onRowSelectionChange={onRowSelectionChange}
          bulkActions={bulkActions}
          filterSlot={
            <>
              {TYPE_TABS.map((tab) => {
                const isActive =
                  tab.value === "all" ? !activeType : activeType === tab.value;
                return (
                  <button
                    key={tab.value}
                    // Clicking the active tab clears it, so one control both
                    // applies and removes the filter.
                    onClick={() =>
                      onTypeChange(
                        tab.value === "all" || isActive ? undefined : tab.value,
                      )
                    }
                    className={`p-2 mx-1 rounded border font-medium flex items-center transition-all duration-150 ${
                      isActive
                        ? "bg-secondary-default text-white"
                        : "bg-white text-secondary-default border-secondary-default hover:bg-secondary-light hover:text-white"
                    }`}
                  >
                    {tab.icon}
                    <span className="ml-1">{tab.label}</span>
                  </button>
                );
              })}
              {toolbarSlot}
            </>
          }
        />
      </div>

      {selectedCreative && (
        <div className="w-full md:w-1/2 flex flex-col items-center transition-all duration-300 mt-6 md:mt-0 relative">
          <button
            onClick={() => onSelectCreative(null)}
            className="absolute top-0 right-0 md:right-4 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close details"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>

          <div className="w-full max-w-md h-[200px] md:w-[420px] md:h-[240px] rounded-lg flex items-center justify-center mb-6 mt-4 md:mt-6">
            <FilePreview
              name={
                selectedCreative.media?.[0]?.fileName ||
                selectedCreative.uniqueId
              }
              type={selectedCreative.media?.[0]?.format || ""}
              url={getFullMediaUrl(selectedCreative.media?.[0]?.fileUrl)}
            />
          </div>

          <div className="w-full max-w-2xl px-2 md:px-0">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold ml-3 text-secondary-700">
                Properties
              </h3>
              {detailActions}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <tbody>
                  {detailRows.map((row) => (
                    <tr key={row.label} className="even:bg-gray-50">
                      <td className="py-2 px-4 font-semibold text-gray-800 w-1/3 border-b border-gray-200">
                        {row.label}
                      </td>
                      <td className="py-2 px-4 text-gray-900 border-b border-gray-200">
                        {row.value}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreativeList;
